<script setup lang="ts">
import { onBeforeUnmount, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterView, useRouter } from 'vue-router'

import { App as CapacitorApp } from '@capacitor/app'
import type { PluginListenerHandle } from '@capacitor/core'

import HelpDialog from '@/components/HelpDialog.vue'
import PurchaseDialog from '@/components/PurchaseDialog.vue'
import { getPatientToken } from '@/lib/auth-token'
import { isVppEdition } from '@/lib/edition'
import { syncOrgLicense } from '@/lib/org-license'
import { onRetirementDetected } from '@/lib/transfer-retirement'
import { useTransferStore } from '@/stores/transfer'
import { isDocked, isNative } from '@/lib/platform'
import { initTelemetry, setTelemetryUser } from '@/lib/telemetry'
import { installVoiceQa } from '@/lib/voice-qa'
import { trackAppLaunched, trackAppResumed } from '@/lib/telemetry-events'
import { useAuthStore } from '@/stores/auth'
import { useDeleteQueueStore } from '@/stores/deleteQueue'
import { useEntitlementStore } from '@/stores/entitlement'
import { useExercisesStore } from '@/stores/exercises'
import { usePatientSettingsStore } from '@/stores/patientSettings'
import { useResumableRunsStore } from '@/stores/resumableRuns'
import { useRunQueueStore } from '@/stores/runQueue'
import { useTopicsStore } from '@/stores/topics'

const i18n = useI18n()
const router = useRouter()
const auth = useAuthStore()
const topics = useTopicsStore()
const exercises = useExercisesStore()
const patientSettings = usePatientSettingsStore()
const runQueue = useRunQueueStore()
const deleteQueue = useDeleteQueueStore()
const entitlement = useEntitlementStore()
const resumableRuns = useResumableRunsStore()
const transfer = useTransferStore()

let appStateListener: PluginListenerHandle | null = null

onMounted(() => {
  // Voice QA corpus (lib/voice-qa.ts): load the cached capture decision, ask
  // the server for the current one, and wire the sample queue's flush hooks.
  // A no-op in builds without VITE_VOICE_QA and on surfaces that never capture
  // (the dock, the phoneme engine). Before the auth bootstrap on purpose — the
  // config read simply fails until a token exists and is retried on the first
  // mic tap.
  void installVoiceQa()

  // The 401 path discovers that this device's account moved to another device;
  // the router's retirement guard only acts on a navigation. Bridge the two so
  // the patient is told immediately rather than after their next tap lands on
  // a screen that cannot load.
  onRetirementDetected(() => {
    if (router.currentRoute.value.name !== 'launch-transferred') {
      void router.replace({ name: 'launch-transferred' })
    }
  })

  // Native (patient) flow only: warm the content library AND the patient's
  // exercise list as soon as the app mounts. Both stores hydrate from the
  // on-disk cache first (instant UI on subsequent launches), then refresh
  // from the server iff a patient token already exists. First-launch users
  // hit these with no token yet — LaunchTerms.onSubmit re-fires preload()
  // after the bootstrap POST returns, which picks up where these calls
  // left off. Both preloads are idempotent via a shared inflight promise.
  if (isNative) {
    // Android safe-area: MainActivity bridges WindowInsets into the CSS
    // --safe-area-inset-* variables on every inset update. The WebView fills
    // the screen edge-to-edge (Android 15 enforces that regardless), and
    // layouts pad themselves via those variables — same model as iOS, where
    // WKWebView populates env(safe-area-inset-*) natively. No JS setup needed.
    void topics.preload()
    void exercises.preload()
    // Patient-global preferences (item order on repeat, help-grid side,
    // speech retries). Reads the local cache for instant paint then
    // refreshes from the server; first-launch users will re-hydrate after
    // bootstrap from LaunchTerms.
    void patientSettings.hydrate()
    // Trial / subscription state — read the on-disk cache first so the
    // home screen renders the correct trial countdown / paywall state
    // immediately on a cold offline launch, then refresh in background.
    // First-launch users hit this with no token yet (the cache will be
    // empty); the background refresh just no-ops and the next call
    // from LaunchTrialPatient.onMounted retries once they're set up.
    void entitlement.hydrate()
    // Subscribes to network + app-state events so any training run that
    // queued offline uploads as soon as connectivity / focus comes back.
    // Idempotent — first call attaches the listeners, repeats no-op.
    void runQueue.install()
    // Same lifecycle hooks for offline-queued exercise deletions —
    // separate queue from runs because deletions are best-effort
    // fire-and-forget while runs carry training payloads.
    void deleteQueue.install()
    // Hydrates the set of exercise_ids that have a resumable mid-run
    // snapshot on disk so the picker can render the "weitermachen" tab.
    // Also purges any snapshot past its 7-day TTL.
    void resumableRuns.install()

    // TelemetryDeck — anonymous analytics for the patient build. The
    // wrapper is a no-op when VITE_TELEMETRYDECK_APP_ID is blank, so dev
    // builds (and the therapist web build, where the env is not set) emit
    // nothing. Defaults provider reads live state from the entitlement
    // store + vue-i18n so every signal carries the current platform,
    // locale, and entitlement context. See lib/telemetry.ts.
    initTelemetry({
      defaults: {
        locale: () => String(i18n.locale.value),
        entitlementStatus: () => entitlement.effectiveStatus,
        trialDaysRemaining: () => entitlement.trialDaysRemaining,
      },
    })
    void (async () => {
      // First-session detection rides on whether the eternal patient token
      // has been minted yet — pre-bootstrap launches are by definition the
      // patient's first visit. Subsequent launches always hit a stored
      // token because Preferences survives across updates (only an uninstall
      // clears it). `app.launched` is always cold here — the alternative
      // path (`app.resumed`) fires from the appStateChange listener below.
      const token = await getPatientToken()
      trackAppLaunched({ isFirstSession: token === null, coldStart: true })
      // Returning launches: identify the user with the eternal token so
      // their signals stitch under a stable hash. TelemetryDeck SHA-256s
      // clientUser before transmission, so the token never leaves the
      // device in the clear. New users hit setTelemetryUser later, from
      // auth.bootstrapPatient() once the token is minted on terms-accept.
      // A transfer whose response never arrived leaves this device holding a
      // dead token and a pending record. Finishing it here, before anything
      // reads the account, means the only visible symptom of a dropped
      // response is that the transfer completes on the next launch instead of
      // immediately. Resolves false on every other launch. See
      // docs/data-transfer.md "Why device_nonce".
      if (await transfer.resumePendingRedemption()) return

      if (token !== null) {
        setTelemetryUser(token)
        // Re-hydrate the auth store from /patient/me. The onboarding gate
        // skips LaunchTerms (the only other bootstrapPatient caller) once a
        // token exists, so without this the linked-practice state — and the
        // hasTherapist-gated "Hausaufgaben" button — is lost on every
        // restart. bootstrapPatient is idempotent on a returning launch.
        void auth.bootstrapPatient()
        // A device managed by a facility's MDM carries its volume-license
        // token in the managed app configuration. Redeem it here so a fleet
        // iPad is licensed before anyone touches it. Silent and idempotent —
        // see lib/org-license.ts.
        void syncOrgLicense()
      }
    })()
    void (async () => {
      try {
        appStateListener = await CapacitorApp.addListener(
          'appStateChange',
          ({ isActive }) => {
            if (isActive) trackAppResumed()
          },
        )
      } catch (e) {
        // Capacitor App plugin can be unavailable in some test/web shells.
        // The launch signal is still useful on its own; resume tracking
        // degrades silently.
        console.warn('App.vue: appStateChange listener registration failed', e)
      }
    })()
  } else if (isDocked) {
    // Web build running inside a Videosprechstunde's Session App Dock. A
    // different shell from the portal embed, with a different handshake and a
    // much narrower session — mount the dock bridge and NOT the embed one, or
    // we would be announcing ourselves to a portal that is not there while the
    // shell waits for an answer that never comes.
    void import('@/composables/useDockBridge').then(({ useDockBridge }) => {
      useDockBridge().mount()
    })
  } else if (window.top !== window.self) {
    // Therapist web build running inside the portal iframe. Mount the embed
    // bridge globally — once, app-wide — so the handshake and session:refresh
    // run for the whole app lifetime, not just while the /embed screen is
    // visible. Without this the bridge died on the first navigation to the
    // workspace routes and the Sanctum session could never be refreshed.
    // Dynamic import keeps the embed code (and the therapist telemetry module)
    // out of the native patient bundle.
    void Promise.all([
      import('@/composables/useEmbedBridge'),
      import('@/lib/telemetry-events-therapist'),
    ]).then(([{ useEmbedBridge }, therapistEvents]) => {
      const bridge = useEmbedBridge()

      // TelemetryDeck — therapist web build. Reports to a SEPARATE TelemetryDeck
      // app (VITE_TELEMETRYDECK_APP_ID_THERAPIST) so therapist and patient
      // dashboards never mix; no-op when that env is blank. entitlement/trial
      // are patient-only and intentionally absent here. practice_id is read
      // lazily so it's omitted on the launch signal (fired before the handshake)
      // and present on every post-ready signal. See lib/telemetry.ts.
      initTelemetry({
        app: 'therapist',
        defaults: {
          locale: () => String(i18n.locale.value),
          role: () => 'therapist',
          practiceId: () => bridge.practiceId.value || null,
        },
      })
      therapistEvents.trackTherapistAppLaunched()

      // Identify the therapist (opaque portal id, SHA-256'd by the SDK) and fire
      // the ready signal once the handshake completes. immediate + a one-shot
      // guard cover the race where the handshake finishes before this import
      // resolves (same pattern as the route-restore watch below).
      let identified = false
      watch(
        bridge.connectionState,
        (state) => {
          if (identified || state !== 'ready' || !bridge.therapistId.value) return
          identified = true
          setTelemetryUser(bridge.therapistId.value)
          therapistEvents.trackTherapistAppReady()
        },
        { immediate: true },
      )

      bridge.mount()

      // Deep-link round-trip. The iframe's route lives in its own (hidden)
      // history, not the portal's address bar, so a browser reload would
      // otherwise drop the therapist back to the default landing. We mirror
      // every navigation up to the portal, which persists it into its URL and
      // replays it as `initialRoute` on the next load.

      // Outbound: report each in-app route to the portal (no-op until ready;
      // deduped in the bridge).
      router.afterEach((to) => bridge.notifyRoute(to.fullPath))

      // Inbound: restore the route the portal handed back, once, when the
      // handshake first reaches 'ready'. `immediate` covers the race where
      // the handshake completes before this dynamic import resolves.
      let restored = false
      watch(
        [bridge.connectionState, bridge.caseloadReceived, bridge.initialRoute],
        ([state, caseloadIn, target]) => {
          // Defer the restore until the session is fully usable: ready AND the
          // portal's first caseload has landed (which only happens once the vault
          // is unlocked). Restoring on `ready` alone drops the iframe onto a
          // patient-context screen (e.g. the chooser) while it's still empty/
          // locked — the failure seen when reloading on the patient picker.
          // EmbedView gates its own redirect the same way; this keeps the
          // deep-link path consistent. Until then EmbedView shows the connecting/
          // vault-locked status.
          if (restored || state !== 'ready' || !caseloadIn || !target) return
          restored = true // consume this handshake's initialRoute exactly once
          // `target` is portal-supplied but already passed the strict origin
          // check on embed:hello, and is only ever used as a vue-router path
          // (never location/innerHTML). Validate defensively anyway.
          if (!target.startsWith('/')) return
          const resolved = router.resolve(target)
          if (resolved.matched.length === 0) return // unknown route → keep default landing
          if (resolved.name === 'embed') return // already the landing; nothing to restore
          void router.replace(target)
        },
        { immediate: true },
      )
    })
  } else if (import.meta.env.DEV) {
    // ⚠️ TEMPORARY dev scaffolding — see lib/dev-caseload.ts.
    // Standalone `npm run dev` has no portal iframe, so no caseload arrives and
    // the patient screens render empty. Seed a fake one. The API itself is NOT
    // faked: VITE_DEV_THERAPIST_JWT authenticates against the real backend, so
    // what you see is real data (seed it with DevTherapistExercisesSeeder).
    // Stripped from every real build by the DEV guard.
    void import('@/lib/dev-caseload').then(({ seedDevCaseload }) => seedDevCaseload())
  }
})

onBeforeUnmount(() => {
  if (appStateListener !== null) {
    void appStateListener.remove()
    appStateListener = null
  }
})
</script>

<template>
  <RouterView />

  <!-- Single global paywall instance. Visibility is driven by the
       entitlement Pinia store; any screen calls
       useEntitlementStore().openPaywall() to surface it.

       Absent on the VPP edition — that bundle has no IAP products and its
       license was bought by the institution, so there is nothing to sell.
       openPaywall() is inert there too; this keeps the dialog (and the IAP
       plugin it pulls in) out of that build's runtime altogether. -->
  <PurchaseDialog v-if="!isVppEdition" />

  <!-- Single global help dialog. Any screen calls
       useHelpStore().openHelp({ title, body }) to surface it (typically
       from the circular "?" button in its header). -->
  <HelpDialog />
</template>

<style scoped></style>
